'use client';

import React, { useState } from 'react';
import { getCarBrandLogo } from '@/lib/brand-logos';

export interface VehicleBrandLogoProps {
  brand?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'badge' | 'raw' | 'minimal';
  className?: string;
  imgClassName?: string;
  alt?: string;
  showName?: boolean;
}

const SIZE_CONFIGS = {
  xs: {
    badge: 'size-6 p-0.5 rounded-lg',
    img: 'max-h-3.5 max-w-3.5',
    text: 'text-[9px]',
    iconSize: 14,
  },
  sm: {
    badge: 'size-8 p-1 rounded-xl',
    img: 'max-h-5 max-w-5',
    text: 'text-[11px]',
    iconSize: 20,
  },
  md: {
    badge: 'size-10 p-1.5 rounded-xl',
    img: 'max-h-7 max-w-7',
    text: 'text-xs',
    iconSize: 28,
  },
  lg: {
    badge: 'size-14 p-2 rounded-2xl',
    img: 'max-h-10 max-w-10',
    text: 'text-sm',
    iconSize: 40,
  },
  xl: {
    badge: 'size-20 p-3 rounded-3xl',
    img: 'max-h-14 max-w-14',
    text: 'text-base',
    iconSize: 56,
  },
};

export default function VehicleBrandLogo({
  brand,
  size = 'md',
  variant = 'badge',
  className = '',
  imgClassName = '',
  alt,
  showName = false,
}: VehicleBrandLogoProps) {
  const [useFallbackUrl, setUseFallbackUrl] = useState(false);
  const [hasError, setHasError] = useState(false);

  const brandInfo = getCarBrandLogo(brand);
  const displayName = brandInfo?.name || brand || 'Vehicle';
  const displayAlt = alt || `${displayName} official logo`;
  const cfg = SIZE_CONFIGS[size] || SIZE_CONFIGS.md;

  const currentSrc = useFallbackUrl ? brandInfo?.fallbackUrl : brandInfo?.url;

  const handleImageError = () => {
    if (!useFallbackUrl && brandInfo?.fallbackUrl) {
      setUseFallbackUrl(true);
    } else {
      setHasError(true);
    }
  };

  // Render logo image content or monogram fallback
  const renderContent = () => {
    if (brandInfo && !hasError && currentSrc) {
      return (
        <img
          src={currentSrc}
          alt={displayAlt}
          width={cfg.iconSize}
          height={cfg.iconSize}
          className={`object-contain transition-transform group-hover:scale-105 select-none ${cfg.img} ${imgClassName}`}
          loading="lazy"
          decoding="async"
          onError={handleImageError}
        />
      );
    }

    // High-taste monogram fallback when no logo file exists or fails to load
    const monogram = (brand || 'V')
      .split(' ')
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();

    return (
      <span className={`font-black text-slate-800 dark:text-slate-200 tracking-tight select-none ${cfg.text}`}>
        {monogram}
      </span>
    );
  };

  if (variant === 'raw') {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        {renderContent()}
        {showName && (
          <span className="font-semibold text-slate-800 dark:text-slate-200 text-xs">
            {displayName}
          </span>
        )}
      </div>
    );
  }

  // Variant 'badge' or 'minimal'
  // Badge provides a high-contrast white container so colored/dark car logos render crisp on dark surfaces
  const containerClasses =
    variant === 'minimal'
      ? `inline-flex items-center justify-center shrink-0 bg-white/90 dark:bg-slate-800/90 border border-slate-200/60 dark:border-slate-700/60 shadow-2xs ${cfg.badge} ${className}`
      : `inline-flex items-center justify-center shrink-0 bg-white dark:bg-white text-slate-900 border border-slate-200/90 shadow-xs ring-1 ring-black/5 ${cfg.badge} ${className}`;

  return (
    <div className="inline-flex items-center gap-2">
      <div className={containerClasses}>
        {renderContent()}
      </div>
      {showName && (
        <span className="font-semibold text-slate-800 dark:text-slate-200 text-xs truncate">
          {displayName}
        </span>
      )}
    </div>
  );
}
